import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Film, Newspaper, Inbox, ExternalLink } from "lucide-react";
import api, { formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/components/Motion";
import SEO from "@/components/SEO";
import JournalImage from "@/components/JournalImage";
import { Wordmark } from "@/components/Navbar";
import { VIDEO_TYPES } from "@/lib/video";

const inputCls =
  "w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none transition-colors focus:border-orange focus:ring-2 focus:ring-orange/20";

const emptyProject = {
  title: "",
  category: "original",
  type: "Feature",
  year: new Date().getFullYear(),
  client: "",
  director: "",
  tagline: "",
  synopsis: "",
  director_note: "",
  poster: "",
  stillsText: "",
  trailer_url: "",
  video_type: "Other",
  imdb_url: "",
  creditsText: "",
  awardsText: "",
  status: "In Development",
  service_scope: "",
  service_locations: "",
  service_challenge: "",
  service_outcome: "",
  featured: false,
  in_development: true,
  published: true,
};

const emptyPost = {
  title: "", category: "Studio", excerpt: "", contentText: "", image: "", author: "Incolour Films", published: true,
};

function projectToForm(p) {
  return {
    ...p,
    in_development: !!p.in_development,
    client: p.client || "",
    director: p.director || "",
    tagline: p.tagline || "",
    director_note: p.director_note || "",
    trailer_url: p.trailer_url || "",
    video_type: p.video_type || "Other",
    imdb_url: p.imdb_url || "",
    status:
      p.status ||
      (p.category === "original"
        ? p.in_development
          ? "In Development"
          : "Released"
        : ""),
    service_scope: p.service_scope || "",
    service_locations: p.service_locations || "",
    service_challenge: p.service_challenge || "",
    service_outcome: p.service_outcome || "",
    stillsText: (p.stills || []).join("\n"),
    creditsText: (p.credits || []).map((c) => `${c.role} | ${c.name}`).join("\n"),
    awardsText: (p.awards || []).join("\n"),
  };
}

function formToProject(f) {
  const isOriginal = f.category === "original";
  const inDevelopment =
    isOriginal &&
    ["In Development", "In Production"].includes(f.status);

  return {
    title: f.title,
    category: f.category,
    type: f.type,
    year: Number(f.year) || new Date().getFullYear(),
    client: f.client || null,
    director: f.director || null,
    tagline: f.tagline || null,
    synopsis: f.synopsis,
    director_note: f.director_note || null,
    poster: f.poster,
    stills: f.stillsText.split("\n").map((s) => s.trim()).filter(Boolean),
    trailer_url: f.trailer_url || null,
    video_type: f.trailer_url ? f.video_type || "Other" : null,
    imdb_url: f.imdb_url || null,
    credits: f.creditsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [role, ...rest] = l.split("|");
      return { role: role.trim(), name: rest.join("|").trim() };
    }).filter((c) => c.role && c.name),
    awards: f.awardsText.split("\n").map((s) => s.trim()).filter(Boolean),
    status: isOriginal ? f.status || null : null,
    service_scope: !isOriginal ? f.service_scope || null : null,
    service_locations: !isOriginal ? f.service_locations || null : null,
    service_challenge: !isOriginal ? f.service_challenge || null : null,
    service_outcome: !isOriginal ? f.service_outcome || null : null,
    featured: !!f.featured,
    in_development: inDevelopment,
    published: !!f.published,
  };
}

function postToForm(p) {
  return { ...p, contentText: (p.content || []).join("\n\n") };
}

function formToPost(f) {
  return {
    title: f.title,
    category: f.category,
    excerpt: f.excerpt,
    content: f.contentText.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
    image: f.image,
    author: f.author,
    published: !!f.published,
  };
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function ProjectForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setF((p) => {
      if (k === "category") {
        return {
          ...p,
          category: value,
          status:
            value === "original"
              ? p.status || "In Development"
              : "",
        };
      }

      return { ...p, [k]: value };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formToProject(f);
      if (f.id) await api.put(`/admin/projects/${f.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success(f.id ? "Project updated" : "Project created");
      onSave();
    } catch (err) {
      toast.error("Save failed — check required fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="cms-project-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title *">
          <input
            required
            value={f.title}
            onChange={set("title")}
            className={inputCls}
            data-testid="cms-project-title-input"
          />
        </Field>

        <Field label="Category">
          <select
            value={f.category}
            onChange={set("category")}
            className={inputCls}
            data-testid="cms-project-category-select"
          >
            <option value="original">Original Work</option>
            <option value="service">Service Production</option>
          </select>
        </Field>

        <Field label="Format">
          <select value={f.type} onChange={set("type")} className={inputCls}>
            {["Feature", "Documentary", "Short", "Commercial", "PSA", "Music Video", "Series", "Other"].map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </Field>

        <Field label="Year">
          <input
            type="number"
            value={f.year}
            onChange={set("year")}
            className={inputCls}
          />
        </Field>

        {f.category === "original" ? (
          <Field label="Project status">
            <select
              value={f.status}
              onChange={set("status")}
              className={inputCls}
              data-testid="cms-project-status-select"
            >
              <option>In Development</option>
              <option>In Production</option>
              <option>Festival Run</option>
              <option>Released</option>
            </select>
          </Field>
        ) : (
          <Field label="Client">
            <input
              value={f.client}
              onChange={set("client")}
              className={inputCls}
            />
          </Field>
        )}

        <Field label="Director">
          <input
            value={f.director}
            onChange={set("director")}
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Tagline">
        <input value={f.tagline} onChange={set("tagline")} className={inputCls} />
      </Field>

      <Field label={f.category === "service" ? "Production overview *" : "Synopsis *"}>
        <textarea
          required
          rows={4}
          value={f.synopsis}
          onChange={set("synopsis")}
          className={inputCls}
        />
      </Field>

      {f.category === "original" ? (
        <Field label="Director's note">
          <textarea
            rows={3}
            value={f.director_note}
            onChange={set("director_note")}
            className={inputCls}
          />
        </Field>
      ) : (
        <div className="space-y-5 rounded-2xl border border-border p-5">
          <div>
            <p className="font-display text-lg font-bold">Service case study</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Optional details that help the public project page explain what Incolour handled.
            </p>
          </div>

          <Field label="Incolour scope">
            <textarea
              rows={3}
              value={f.service_scope}
              onChange={set("service_scope")}
              className={inputCls}
              placeholder="e.g. Local producing, crew, camera, transport, locations, production logistics"
            />
          </Field>

          <Field label="Locations">
            <textarea
              rows={2}
              value={f.service_locations}
              onChange={set("service_locations")}
              className={inputCls}
              placeholder="e.g. Toronto, Hamilton, studio and exterior locations"
            />
          </Field>

          <Field label="Production challenge / requirements">
            <textarea
              rows={3}
              value={f.service_challenge}
              onChange={set("service_challenge")}
              className={inputCls}
              placeholder="What did the production need Incolour to solve or coordinate?"
            />
          </Field>

          <Field label="Outcome">
            <textarea
              rows={3}
              value={f.service_outcome}
              onChange={set("service_outcome")}
              className={inputCls}
              placeholder="What was delivered or accomplished?"
            />
          </Field>
        </div>
      )}

      <Field label="Poster image URL *"><input required value={f.poster} onChange={set("poster")} className={inputCls} placeholder="https://…" data-testid="cms-project-poster-input" /></Field>
      <Field label="Stills URLs — one per line"><textarea rows={3} value={f.stillsText} onChange={set("stillsText")} className={inputCls} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Video URL (YouTube or Vimeo)">
          <input
            value={f.trailer_url}
            onChange={set("trailer_url")}
            className={inputCls}
            placeholder="https://www.youtube.com/watch?v=…"
          />
        </Field>

        <Field label="Video type">
          <select
            value={f.video_type}
            onChange={set("video_type")}
            className={inputCls}
            disabled={!f.trailer_url}
          >
            {VIDEO_TYPES.map((type) => (
              <option key={type}>{type}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="IMDb URL (optional)"><input value={f.imdb_url} onChange={set("imdb_url")} className={inputCls} placeholder="https://www.imdb.com/title/tt…" data-testid="cms-project-imdb-input" /></Field>
      <Field label="Credits — one per line, e.g. Director | Jane Doe or Starring | Jane Doe"><textarea rows={4} value={f.creditsText} onChange={set("creditsText")} className={inputCls} /></Field>
      <Field label="Awards — one per line"><textarea rows={2} value={f.awardsText} onChange={set("awardsText")} className={inputCls} /></Field>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={set("featured")} className="h-4 w-4 accent-[#FF5A1F]" data-testid="cms-project-featured-check" /> Featured on homepage</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} className="h-4 w-4 accent-[#FF5A1F]" /> Published</label>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="rounded-full bg-orange px-6 py-3 text-sm font-semibold text-cream disabled:opacity-60" data-testid="cms-project-save-btn">
          {saving ? "Saving…" : "Save project"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-border px-6 py-3 text-sm font-semibold">Cancel</button>
      </div>
    </form>
  );
}

function PostForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formToPost(f);
      if (f.id) await api.put(`/admin/journal/${f.id}`, payload);
      else await api.post("/admin/journal", payload);
      toast.success(f.id ? "Article updated" : "Article created");
      onSave();
    } catch {
      toast.error("Save failed — check required fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="cms-journal-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title *"><input required value={f.title} onChange={set("title")} className={inputCls} data-testid="cms-journal-title-input" /></Field>
        <Field label="Category">
          <select value={f.category} onChange={set("category")} className={inputCls}>
            {["Guides", "Case Studies", "Studio", "Behind the Scenes", "Release", "Festival", "Community"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Author"><input value={f.author} onChange={set("author")} className={inputCls} /></Field>
        <Field label="Hero image URL *"><input required value={f.image} onChange={set("image")} className={inputCls} placeholder="https://…" /></Field>
      </div>
      <Field label="Excerpt"><textarea rows={2} value={f.excerpt} onChange={set("excerpt")} className={inputCls} /></Field>
      <Field label="Body — separate blocks with a blank line">
        <textarea
          required
          rows={12}
          value={f.contentText}
          onChange={set("contentText")}
          className={inputCls}
          data-testid="cms-journal-content-input"
          placeholder={"Paragraph text\n\n## Section heading\n\n- List item one\n- List item two\n\n[Internal link](/service) or [External link](https://example.com)"}
        />
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          Use ## for section headings, ### for smaller headings, lines starting with - for lists, and [label](URL) for links.
        </p>
      </Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} className="h-4 w-4 accent-[#FF5A1F]" /> Published</label>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="rounded-full bg-orange px-6 py-3 text-sm font-semibold text-cream disabled:opacity-60" data-testid="cms-journal-save-btn">
          {saving ? "Saving…" : "Save article"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-border px-6 py-3 text-sm font-semibold">Cancel</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  usePageTitle("CMS");
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [editing, setEditing] = useState(null); // {kind:'project'|'post', data}

  const loadProjects = useCallback(() => api.get("/admin/projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : (r.data?.projects || []))).catch(() => setProjects([])), []);
  const loadPosts = useCallback(() => api.get("/admin/journal").then((r) => setPosts(Array.isArray(r.data) ? r.data : (r.data?.posts || r.data?.journal || []))).catch(() => setPosts([])), []);

  useEffect(() => {
    const token = localStorage.getItem("incolour_token");
    if (!token) return;
    loadProjects();
    loadPosts();
  }, [user, loadProjects, loadPosts]);

  useEffect(() => {
    const token = localStorage.getItem("incolour_token");
    if (token && tab === "inquiries") {
      api.get("/admin/inquiries")
        .then((r) => setInquiries(Array.isArray(r.data) ? r.data : (r.data?.inquiries || [])))
        .catch(() => setInquiries([]));
    }
  }, [tab, user]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const delProject = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/projects/${p.id}`);
    toast.success("Project deleted");
    loadProjects();
  };

  const delPost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/journal/${p.id}`);
    toast.success("Article deleted");
    loadPosts();
  };

  const projectList = Array.isArray(projects) ? projects : [];
  const postList = Array.isArray(posts) ? posts : [];
  const inquiryList = Array.isArray(inquiries) ? inquiries : [];

  const TABS = [
    { k: "projects", label: "Projects", icon: Film, count: projectList.length },
    { k: "journal", label: "Journal", icon: Newspaper, count: postList.length },
    { k: "inquiries", label: "Inquiries", icon: Inbox, count: null },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="cms-dashboard">
      <SEO title="CMS" noindex />
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/"><Wordmark /></Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-orange" data-testid="cms-view-site-link">
              <ExternalLink size={14} /> View site
            </Link>
            <span className="hidden text-sm text-muted-foreground sm:block">{user?.email || "Admin"}</span>
            <button onClick={logout} className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-coral hover:text-coral" data-testid="cms-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button
                key={t.k}
                onClick={() => { setTab(t.k); setEditing(null); }}
                data-testid={`cms-tab-${t.k}`}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                  tab === t.k ? "border-ink bg-ink text-cream dark:border-cream dark:bg-cream dark:text-ink" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <t.icon size={15} /> {t.label}{t.count != null && <span className="text-orange"> {t.count}</span>}
              </button>
            ))}
          </div>
          {tab === "projects" && (
            <button onClick={() => setEditing({ kind: "project", data: { ...emptyProject } })} data-testid="cms-add-project-btn" className="flex items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-cream">
              <Plus size={15} /> Add project
            </button>
          )}
          {tab === "journal" && (
            <button onClick={() => setEditing({ kind: "post", data: { ...emptyPost } })} data-testid="cms-add-post-btn" className="flex items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-cream">
              <Plus size={15} /> Add article
            </button>
          )}
        </div>

        {editing && (
          <div className="mt-8 rounded-2xl border border-orange/40 bg-cream2 p-6 dark:bg-card sm:p-8" data-testid="cms-editor-panel">
            <h2 className="mb-6 font-display text-xl font-bold">
              {editing.kind === "project" ? (editing.data.id ? "Edit project" : "New project") : editing.data.id ? "Edit article" : "New article"}
            </h2>
            {editing.kind === "project" ? (
              <ProjectForm initial={editing.data} onSave={() => { setEditing(null); loadProjects(); }} onCancel={() => setEditing(null)} />
            ) : (
              <PostForm initial={editing.data} onSave={() => { setEditing(null); loadPosts(); }} onCancel={() => setEditing(null)} />
            )}
          </div>
        )}

        {tab === "projects" && (
          <div className="mt-8 divide-y divide-border overflow-hidden rounded-2xl border border-border" data-testid="cms-projects-list">
            {projectList.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-background p-4">
                <img src={p.poster} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display font-bold">{p.title}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">
                    <span className={p.category === "original" ? "text-orange" : "text-blue"}>{p.category}</span> · {p.type} · {p.year}
                    {p.category === "original" && (
                      <span className="text-orange"> · {p.status || (p.in_development ? "In Development" : "Released")}</span>
                    )}
                    {p.category === "service" && p.client && (
                      <span> · {p.client}</span>
                    )}
                    {p.featured && <span className="text-orange"> · Featured</span>}
                    {!p.published && <span className="text-coral"> · Draft</span>}
                  </p>
                </div>
                <button onClick={() => setEditing({ kind: "project", data: projectToForm(p) })} className="rounded-full border border-border p-2.5 hover:border-orange hover:text-orange" aria-label={`Edit ${p.title}`} data-testid={`cms-edit-project-${p.slug}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => delProject(p)} className="rounded-full border border-border p-2.5 hover:border-coral hover:text-coral" aria-label={`Delete ${p.title}`} data-testid={`cms-delete-project-${p.slug}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {projects.length === 0 && <p className="p-8 text-center text-muted-foreground">No projects yet.</p>}
          </div>
        )}

        {tab === "journal" && (
          <div className="mt-8 divide-y divide-border overflow-hidden rounded-2xl border border-border" data-testid="cms-journal-list">
            {postList.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-background p-4">
                <JournalImage compact src={p.image} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display font-bold">{p.title}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">
                    {p.category} · {formatDate(p.published_at)}{!p.published && <span className="text-coral"> · Draft</span>}
                  </p>
                </div>
                <button onClick={() => setEditing({ kind: "post", data: postToForm(p) })} className="rounded-full border border-border p-2.5 hover:border-orange hover:text-orange" aria-label={`Edit ${p.title}`} data-testid={`cms-edit-post-${p.slug}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => delPost(p)} className="rounded-full border border-border p-2.5 hover:border-coral hover:text-coral" aria-label={`Delete ${p.title}`} data-testid={`cms-delete-post-${p.slug}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {posts.length === 0 && <p className="p-8 text-center text-muted-foreground">No articles yet.</p>}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="mt-8 space-y-4" data-testid="cms-inquiries-list">
            {inquiryList.map((q) => (
              <div key={q.id} className="rounded-2xl border border-border bg-background p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display font-bold">{q.name} <span className="text-sm font-normal text-muted-foreground">· {q.email}{q.company ? ` · ${q.company}` : ""}</span></p>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{formatDate(q.created_at)}</p>
                </div>
                <p className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-orange">{[q.project_type, q.budget].filter(Boolean).join(" · ") || "General inquiry"}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{q.message}</p>
              </div>
            ))}
            {inquiries.length === 0 && <p className="rounded-2xl border border-border p-8 text-center text-muted-foreground">No inquiries yet — they'll land here when the contact form is used.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
